import { useTranslation } from 'react-i18next'
import { ChevronDown, Pencil } from 'lucide-react'
import { COUNTRIES, SOCIAL_PLATFORMS } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Input, inputVariants } from '@/components/ui/input'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { TrackerSettingsStepProps } from '@/types/components/social-tracker-dialog'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { SocialTrackerLanguageField } from './SocialTrackerLanguageField'
import { SocialTermsInput } from './SocialTermsInput'
import {
  SocialRelevanceFields,
  SocialThemeFields,
} from './SocialRelevanceFields'
import { SOCIAL_PLATFORM_LABELS } from './socialFormat'
import {
  labelValues,
  withLabelValues,
  spacedHashtagKeywords,
} from './socialTrackerForm'

export function SocialTrackerSettingsStep({
  form,
  updateField,
  drafts,
  updateDraft,
  missing,
  fieldError,
  isEditing,
  onEditTopics,
}: TrackerSettingsStepProps) {
  const { t, i18n } = useTranslation('app')
  const spacedKeywords = spacedHashtagKeywords(form)
  return (
    <div
      className="flex min-w-0 flex-col gap-5"
      data-testid="social-tracker-settings-step"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3 rounded-xl bg-muted/50 p-3">
          <Text size="sm" className="min-w-0 truncate">
            {form.keywords.join(', ')}
          </Text>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-auto shrink-0 gap-1.5 p-0 text-muted-foreground"
            data-testid="social-tracker-edit-topics"
            onClick={onEditTopics}
          >
            <Pencil className="h-3 w-3" />
            {t('socialListening.wizard.editTopics')}
          </Button>
        </div>
        <SocialRelevanceFields
          brief={form.brief}
          onBriefChange={(value) => updateField('brief', value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="social-tracker-name">
          {t('socialListening.form.name')}
        </Label>
        <Input
          id="social-tracker-name"
          maxLength={255}
          value={form.name}
          placeholder={t('socialListening.form.namePlaceholder')}
          aria-invalid={missing.includes('name') || undefined}
          aria-describedby={
            missing.includes('name') ? 'social-tracker-name-error' : undefined
          }
          data-testid="social-tracker-name-input"
          onChange={(event) => updateField('name', event.target.value)}
        />
        {fieldError('name')}
      </div>

      <fieldset className="space-y-1.5">
        <legend className="mb-1.5 text-sm font-medium">
          {t('socialListening.form.platform')}
        </legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {SOCIAL_PLATFORMS.map((platform) => {
            const isSelected = form.platform === platform
            return (
              <button
                key={platform}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={isEditing}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-[6px] border px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-primary/45 bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
                data-testid={`social-tracker-platform-${platform}`}
                onClick={() => {
                  updateField('platform', platform)
                }}
              >
                <SocialPlatformIcon platform={platform} />
                {SOCIAL_PLATFORM_LABELS[platform]}
              </button>
            )
          })}
        </div>
        {isEditing ? (
          <Text as="span" size="xs" color="muted" className="block">
            {t('socialListening.form.platformLocked')}
          </Text>
        ) : null}
      </fieldset>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="social-tracker-language">
            {t('socialListening.form.language')}
          </Label>
          <SocialTrackerLanguageField
            value={form.language}
            onChange={(value) => updateField('language', value)}
            invalid={missing.includes('language')}
          />
          {fieldError('language')}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="social-tracker-market">
            {t('socialListening.form.market')}
          </Label>
          <div className="relative">
            <select
              id="social-tracker-market"
              value={form.market}
              className={cn(inputVariants(), 'appearance-none pr-10')}
              aria-invalid={missing.includes('market') || undefined}
              aria-describedby={
                missing.includes('market')
                  ? 'social-tracker-market-error'
                  : undefined
              }
              data-testid="social-tracker-market-input"
              onChange={(event) => updateField('market', event.target.value)}
            >
              <option value="" disabled>
                {t('socialListening.form.selectMarket')}
              </option>
              {!COUNTRIES.some((country) => country.name === form.market) &&
              form.market ? (
                <option value={form.market}>
                  {localizedCountryName(form.market, i18n.language)}
                </option>
              ) : null}
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.name}>
                  {localizedCountryName(country.code, i18n.language)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
          {fieldError('market')}
        </div>
      </div>

      <fieldset className="space-y-3" data-testid="social-tracker-labels">
        <legend className="mb-0.5 text-sm font-medium">
          {t('socialListening.form.labels')}
        </legend>
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          {(['brand', 'category'] as const).map((kind) => (
            <div key={kind} className="space-y-1.5">
              <Label htmlFor={`social-tracker-label-${kind}`}>
                {t(`socialListening.form.labelKind.${kind}`)}
              </Label>
              <SocialTermsInput
                id={`social-tracker-label-${kind}`}
                values={labelValues(form.labels, kind)}
                placeholder={t(`socialListening.form.labelPlaceholder.${kind}`)}
                testId={`social-tracker-label-${kind}-input`}
                draft={drafts[kind]}
                onDraftChange={(value) => updateDraft(kind, value)}
                onChange={(values) =>
                  updateField(
                    'labels',
                    withLabelValues(form.labels, kind, values),
                  )
                }
              />
            </div>
          ))}
        </div>
      </fieldset>

      {form.platform === 'instagram' ? (
        <Text
          size="xs"
          color="muted"
          data-testid="social-tracker-keywords-hint"
        >
          {t('socialListening.form.instagramKeywordsHint')}
        </Text>
      ) : null}
      {spacedKeywords.length ? (
        <Text
          size="xs"
          className="text-destructive"
          role="alert"
          data-testid="social-tracker-hashtag-error"
        >
          {t('socialListening.form.instagramKeywordsSpaces', {
            keywords: spacedKeywords.join(', '),
          })}
        </Text>
      ) : null}
      <details className="border-t border-border pt-4">
        <summary className="cursor-pointer text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {t('socialListening.form.advancedSettings')}
        </summary>
        <div className="divide-y divide-border pt-2">
          <details className="py-4">
            <summary className="cursor-pointer text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {t('socialListening.form.handles')}
            </summary>
            <div className="pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="social-tracker-handles">
                  {t('socialListening.form.handles')}
                </Label>
                <SocialTermsInput
                  id="social-tracker-handles"
                  values={form.handles}
                  placeholder={t(
                    `socialListening.form.handlesPlaceholder.${form.platform}`,
                  )}
                  testId="social-tracker-handles-input"
                  draft={drafts.handles}
                  onDraftChange={(value) => updateDraft('handles', value)}
                  onChange={(values) => updateField('handles', values)}
                />
                <Text as="span" size="xs" color="muted" className="block">
                  {t('socialListening.form.handlesHint')}
                </Text>
              </div>
            </div>
          </details>
          <SocialThemeFields
            topics={form.topics}
            onTopicsChange={(value) => updateField('topics', value)}
          />
        </div>
      </details>
    </div>
  )
}
